import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import { FlakesTexture } from 'three/examples/jsm/textures/FlakesTexture.js'

export interface MaterialDefinition {
  type?: string
  parent?: string
  color?: number
  roughness?: number
  metalness?: number
  map?: string
  metalnessMap?: string
  roughnessMap?: string
  bumpMap?: string
  bumpScale?: number
  aoMap?: string
  alphaMap?: string
  normalMap?: string
  clearcoatNormalMap?: string
  transmissionMap?: string
  thicknessMap?: string
  specularIntensityMap?: string
  specularColorMap?: string
  normalScale?: [number, number]
  transparent?: boolean
  opacity?: number
  envMapIntensity?: number
  clearcoat?: number
  clearcoatRoughness?: number
  sheen?: number
  sheenRoughness?: number
  transmission?: number
  thickness?: number
  attenuationDistance?: number
  attenuationColor?: number
  ior?: number
  reflectivity?: number
  specularIntensity?: number
  specularColor?: number
  emissive?: number
  emissiveIntensity?: number
  emissiveMap?: string
  depthTest?: boolean
  depthWrite?: boolean
}

export interface TextureDefinition {
  url: string
  type?: string
  colorSpace?: string
  channel?: number
  repeat?: [number, number]
  wrapS?: number
  wrapT?: number
  magFilter?: number
  minFilter?: number
  offset?: [number, number]
  rotation?: number
  anisotropy?: number
}

export interface MaterialDatabase {
  global: {
    materials: { [key: string]: MaterialDefinition }
    textures: { [key: string]: TextureDefinition }
  }
}

export const AudioAssetMap = {
  'lid-close': 'Lid_Close.wav',
  'lid-open': 'Lid_Open.wav',
  'click-graphic-close': 'click_closeGraphic.wav',
  'click-graphic-open': 'click_openGraphic.wav',
  'click-physical-button': 'click_physicalButton.wav',
  'engine-25-loop': 'engine_25percent_loop.wav',
  'engine-25-to-100-transition': 'engine_25to100.wav',
  // 'engine-25-to-0-transition': 'engine_25toOff.wav',
  'engine-25-to-0-transition': 'engine_25toOff_v3.wav',
  'engine-100-loop': 'engine_100percent_loop.wav',
  // 'engine-100-to-0-transition': 'engine_100toOff.wav',
  'engine-100-to-0-transition': 'engine_100toOff_v3.wav',
  'engine-100-to-25-transition': 'engine_100to25.wav',
  'engine-start-to-25-transition': 'engine_startTo25.wav',
  'engine-emergency-stop': 'engine_emergencyshutdown.wav',
}

export class AssetManager {
  private gltfLoader: GLTFLoader
  private dracoLoader: DRACOLoader
  private rgbeLoader: RGBELoader
  private audioLoader: THREE.AudioLoader
  private materialDatabase: MaterialDatabase | null = null
  private materialCache: Map<string, THREE.Material> = new Map()
  private textureCache: Map<string, THREE.Texture> = new Map()
  private audioCache: Map<string, AudioBuffer> = new Map()
  private environmentTexture: THREE.Texture | null = null

  private _modelCache: Map<string, THREE.Group> = new Map()

  constructor() {
    // Initialize loaders
    this.gltfLoader = new GLTFLoader()
    this.dracoLoader = new DRACOLoader()
    this.rgbeLoader = new RGBELoader()
    this.audioLoader = new THREE.AudioLoader()
    
    // Configure DRACO loader
    this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
    this.gltfLoader.setDRACOLoader(this.dracoLoader)
  }

  /**
   * Load material database from JSON file
   */
  async loadMaterialDatabase(): Promise<void> {
    try {
      // console.log('🔄 Loading material database...')
      const response = await fetch('./db.json')
      this.materialDatabase = await response.json()
      // console.log('✅ Material database loaded successfully')
    } catch (error) {
      // console.error('❌ Failed to load material database:', error)
      throw error; // Re-throw to allow caller to handle the error
    }
  }

  /**
   * Check if material database is loaded
   */
  get isMaterialDatabaseLoaded(): boolean {
    return this.materialDatabase !== null;
  }

  /**
   * Get material by name from cache or create new instance from database
   * @param materialName - The name/token of the material to fetch
   * @returns The material instance or null if not found
   */
  getMaterial(materialName: string): THREE.Material | null {
    if (!this.materialDatabase?.global?.materials) {
      console.warn('No material database available, cannot fetch material:', materialName)
      return null
    }

    // Check cache first
    if (this.materialCache.has(materialName)) {
      // console.log(`🎨 Found material in cache: ${materialName}`)
      return this.materialCache.get(materialName)!
    }

    // Look for material definition in database
    const materialDef = this.materialDatabase.global.materials[materialName]
    
    if (materialDef) {
      // console.log(`🎨 Creating new material from database: ${materialName}`)
      
      // Create new material from definition
      const newMaterial = this.createMaterialFromDefinition(materialName, materialDef)
      
      // Cache the new material
      this.materialCache.set(materialName, newMaterial)
      
      return newMaterial
    } else {
      console.warn(`No material definition found for: ${materialName}`)
      return null
    }
  }

  /**
   * Load GLB model with material application
   */
  async loadGLBModel(modelPath: string): Promise<THREE.Group | null> {
    if (this._modelCache.has(modelPath)) {
      console.log('Model already loaded from cache:', modelPath)
      return this._modelCache.get(modelPath)!
    }
    try {   
      console.log('Loading GLB model:', modelPath)
      const gltf = await this.gltfLoader.loadAsync(modelPath)
      const model = gltf.scene
      
      this._modelCache.set(modelPath, model)

      // Apply materials from JSON database if available
      if (this.materialDatabase) {
        console.log('Applying materials to model:', modelPath)
        await this.applyMaterialsToModel(model)
      } else {
        console.log('No material database available, using default materials')
      }
      
      // Load HDR environment map
      try {
        await this.loadHDREnvironment()
      } catch (error) {
        console.warn('HDR environment map not available:', error)
      }
      return model
      
    } catch (error) {
      console.error('Failed to load GLB model:', error)
      console.error('Model path was:', modelPath)
      
      // If it's a JSON parse error, it means we got HTML instead of GLB
      if (error instanceof SyntaxError && error.message.includes('Unexpected token')) {
        console.error('Received HTML instead of GLB file - check if file exists and path is correct')
        console.error('Make sure the GLB file is in the public/ folder and accessible via HTTP')
      }
      return null
    }
  }

  /**
   * Load HDR environment map
   */
  private async loadHDREnvironment(): Promise<void> {
    try {
      // console.log('🔄 Loading HDR environment map...')
      
      const hdrTexture = await this.rgbeLoader.loadAsync('./global/textures/photo_studio_broadway_hall_1k.hdr')
      hdrTexture.mapping = THREE.EquirectangularReflectionMapping
      
      // Store the environment texture for scene usage
      this.environmentTexture = hdrTexture
      
      // console.log('✅ HDR environment map loaded')
      
      // Update materials for environment
      // this.updateMaterialsForEnvironment(hdrTexture)
      
    } catch (error) {
      console.error('Failed to load HDR environment map:', error)
      throw error
    }
  }

  /**
   * Apply materials to model from database
   */
  private async applyMaterialsToModel(model: THREE.Group): Promise<void> {
    if (!this.materialDatabase?.global?.materials) {
      console.log('No material database available, using default materials')
      return
    }
    
    let materialCount = 0
    
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const materialName = child.material.name || child.material.uuid
        
        // Look for matching material in database
        const materialDef = this.materialDatabase!.global.materials[materialName]
        
        if (materialDef) {
          // Check cache first
          if (this.materialCache.has(materialName)) {
            child.material = this.materialCache.get(materialName)!
          } else {
            // Create new material from definition
            const newMaterial = this.createMaterialFromDefinition(materialName, materialDef)
            this.materialCache.set(materialName, newMaterial)
            child.material = newMaterial
            materialCount++
          }
        } else {
          console.log(`No material definition found for: ${materialName}`)

          // Highlight unmatched materials
          child.material = new THREE.MeshBasicMaterial({
            color: 0xff0000
          })
        }
      }
    })
  }

  /**
   * Create material from definition
   */
  private createMaterialFromDefinition(name: string, materialDef: MaterialDefinition): THREE.Material {
    // Handle parent materials (inheritance)

    let combinedMaterialDef: MaterialDefinition = { }


    if (materialDef.parent && this.materialDatabase?.global?.materials) {
        const parentChain = [];
        let nextParent: string | undefined = materialDef.parent;
        while (nextParent !== undefined) {
            parentChain.push(nextParent);
            nextParent = this.materialDatabase.global.materials[nextParent]?.parent;
        }
        // Merge parent properties with child properties
        // materialDef = { ...parentDef, ...materialDef }
    
        parentChain.reverse();

        for (const parent of parentChain) {
            const parentDef = this.materialDatabase.global.materials[parent];
            combinedMaterialDef = { ...combinedMaterialDef, ...parentDef,  }
            
        }
    }
    combinedMaterialDef = { ...combinedMaterialDef, ...materialDef }
    
    materialDef = combinedMaterialDef

    

    // Determine material type
    const materialType = materialDef.type || 'MeshPhysicalMaterial'
    
    // Create base material
    let material: THREE.Material
    switch (materialType) {
      case 'MeshPhysicalMaterial':
        material = new THREE.MeshPhysicalMaterial()
        break
      case 'MeshStandardMaterial':
        material = new THREE.MeshStandardMaterial()
        break
      case 'MeshLambertMaterial':
        material = new THREE.MeshLambertMaterial()
        break
      case 'MeshBasicMaterial':
        material = new THREE.MeshBasicMaterial()
        break
      default:
        material = new THREE.MeshPhysicalMaterial()
    }

    // Apply properties
    if (materialDef.color !== undefined) {
      (material as any).color.setHex(materialDef.color)
    }
    
    if (materialDef.roughness !== undefined) {
      (material as any).roughness = materialDef.roughness
    }
    
    if (materialDef.metalness !== undefined) {
      (material as any).metalness = materialDef.metalness
    }

    if (materialDef.emissive !== undefined) {   
      (material as any).emissive.setHex(materialDef.emissive)
    }

    if (materialDef.emissiveIntensity !== undefined) {
      (material as any).emissiveIntensity = materialDef.emissiveIntensity
    }

    // Apply depth test and depth write properties
    if (materialDef.depthTest !== undefined) {
      (material as any).depthTest = materialDef.depthTest
    }
    if (materialDef.depthWrite !== undefined) {
      (material as any).depthWrite = materialDef.depthWrite
    }

    // Apply normal scale if defined
    if (materialDef.normalScale !== undefined) {
      ;(material as any).normalScale.set(materialDef.normalScale[0], materialDef.normalScale[1])
    }

    // Apply transparency properties
    if (materialDef.transparent !== undefined) {
      ;(material as any).transparent = materialDef.transparent
    }
    if (materialDef.opacity !== undefined) {
      ;(material as any).opacity = materialDef.opacity
    }

    // Apply environment map intensity
    if (materialDef.envMapIntensity !== undefined) {
      ;(material as any).envMapIntensity = materialDef.envMapIntensity
    }

    // Load and apply textures
    if (materialDef.map && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.map]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).map = texture
      }
    }
    
    if (materialDef.metalnessMap && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.metalnessMap]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).metalnessMap = texture
      }
    }
    
    if (materialDef.roughnessMap && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.roughnessMap]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).roughnessMap = texture
      }
    }
    if (materialDef.emissiveMap && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.emissiveMap]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).emissiveMap = texture
      }
    }
    
    if (materialDef.bumpMap && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.bumpMap]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).bumpMap = texture
      }
    }
    
    if (materialDef.aoMap && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.aoMap]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).aoMap = texture
      }
    }
    
    if (materialDef.alphaMap && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.alphaMap]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).alphaMap = texture
      }
    }
    
    if (materialDef.normalMap && this.materialDatabase?.global?.textures) {
      const textureDef = this.materialDatabase.global.textures[materialDef.normalMap]
      if (textureDef) {
        const texture = this.loadTexture(textureDef)
        ;(material as any).normalMap = texture
      }
    }

    // Apply MeshPhysicalMaterial specific properties
    if (material instanceof THREE.MeshPhysicalMaterial) {
      // Clearcoat properties
      if (materialDef.clearcoat !== undefined) {
        material.clearcoat = materialDef.clearcoat
      }
      if (materialDef.clearcoatRoughness !== undefined) {
        material.clearcoatRoughness = materialDef.clearcoatRoughness
      }
      if (materialDef.clearcoatNormalMap && this.materialDatabase?.global?.textures) {
        const textureDef = this.materialDatabase.global.textures[materialDef.clearcoatNormalMap]
        if (textureDef) {
          const texture = this.loadTexture(textureDef)
          material.clearcoatNormalMap = texture
        }
      }
      
      if (materialDef.bumpScale !== undefined) {
        material.bumpScale = materialDef.bumpScale
      }
  

      // Sheen properties
      if (materialDef.sheen !== undefined) {
        material.sheen = materialDef.sheen
      }
      if (materialDef.sheenRoughness !== undefined) {
        material.sheenRoughness = materialDef.sheenRoughness
      }

      // Transmission properties
      if (materialDef.transmission !== undefined) {
        material.transmission = materialDef.transmission
      }
      if (materialDef.transmissionMap && this.materialDatabase?.global?.textures) {
        const textureDef = this.materialDatabase.global.textures[materialDef.transmissionMap]
        if (textureDef) {
          const texture = this.loadTexture(textureDef)
          material.transmissionMap = texture
        }
      }

      // Thickness properties
      if (materialDef.thickness !== undefined) {
        material.thickness = materialDef.thickness
      }
      if (materialDef.thicknessMap && this.materialDatabase?.global?.textures) {
        const textureDef = this.materialDatabase.global.textures[materialDef.thicknessMap]
        if (textureDef) {
          const texture = this.loadTexture(textureDef)
          material.thicknessMap = texture
        }
      }

      // Attenuation properties
      if (materialDef.attenuationDistance !== undefined) {
        material.attenuationDistance = materialDef.attenuationDistance
      }
      if (materialDef.attenuationColor !== undefined) {
        material.attenuationColor.setHex(materialDef.attenuationColor)
      }

      // IOR and reflectivity
      if (materialDef.ior !== undefined) {
        material.ior = materialDef.ior
      }
      if (materialDef.reflectivity !== undefined) {
        material.reflectivity = materialDef.reflectivity
      }

      // Specular properties
      if (materialDef.specularIntensity !== undefined) {
        material.specularIntensity = materialDef.specularIntensity
      }
      if (materialDef.specularColor !== undefined) {
        material.specularColor.setHex(materialDef.specularColor)
      }
      if (materialDef.specularIntensityMap && this.materialDatabase?.global?.textures) {
        const textureDef = this.materialDatabase.global.textures[materialDef.specularIntensityMap]
        if (textureDef) {
          const texture = this.loadTexture(textureDef)
          material.specularIntensityMap = texture
        }
      }
      if (materialDef.specularColorMap && this.materialDatabase?.global?.textures) {
        const textureDef = this.materialDatabase.global.textures[materialDef.specularColorMap]
        if (textureDef) {
          const texture = this.loadTexture(textureDef)
          material.specularColorMap = texture
        }
      }
    }
    material.name = name
    return material
  }

  /**
   * Load texture from definition
   */
  private loadTexture(textureDef: TextureDefinition): THREE.Texture {
    // Check cache first
    if (this.textureCache.has(textureDef.url)) {
      return this.textureCache.get(textureDef.url)!
    }
    let texture: THREE.Texture;
    if (textureDef.type === 'FlakesTexture') {
        texture = new THREE.CanvasTexture(new FlakesTexture() as unknown as HTMLCanvasElement);
    } else {

        texture = new THREE.TextureLoader().load(textureDef.url, () => {
        }, () => {
        }, () => {  
            console.error("TEXTURE FAILED", textureDef.url)
        })
    }

    
    // Apply texture properties
    if (textureDef.colorSpace !== undefined) {
      texture.colorSpace = textureDef.colorSpace as THREE.ColorSpace
    }
    if (textureDef.channel !== undefined) {
      texture.channel = textureDef.channel
    }
    if (textureDef.repeat !== undefined) {
      texture.repeat.set(textureDef.repeat[0], textureDef.repeat[1])
    }
    if (textureDef.wrapS !== undefined) {
      texture.wrapS = textureDef.wrapS as THREE.Wrapping
    }
    if (textureDef.wrapT !== undefined) {
      texture.wrapT = textureDef.wrapT as THREE.Wrapping
    }
    texture.magFilter = textureDef.magFilter as THREE.MagnificationTextureFilter ?? texture.magFilter
    texture.minFilter = textureDef.minFilter as THREE.MinificationTextureFilter ?? texture.minFilter
    if (textureDef.offset !== undefined) {
      texture.offset.set(textureDef.offset[0], textureDef.offset[1])
    }
    texture.rotation = textureDef.rotation ?? texture.rotation
    texture.anisotropy = textureDef.anisotropy ?? texture.anisotropy
    
    this.textureCache.set(textureDef.url, texture)
    return texture
  }

  /**
   * Load audio file from URL
   * @param audioUrl - The URL of the audio file to load
   * @returns Promise that resolves to AudioBuffer
   */
  async loadAudio(audioUrl: string): Promise<AudioBuffer | null> {
    try {
      // console.log('🔊 Loading audio file...', audioUrl)
      
      // Check cache first
      if (this.audioCache.has(audioUrl)) {
        // console.log('🔊 Found audio in cache:', audioUrl)
        return this.audioCache.get(audioUrl)!
      }

      // Load audio using AudioLoader
      const audioBuffer = await this.audioLoader.loadAsync(audioUrl)
      
      // Cache the audio buffer
      this.audioCache.set(audioUrl, audioBuffer)
      
      // console.log('✅ Audio loaded successfully:', audioUrl)
      return audioBuffer
      
    } catch (error) {
      console.error('Failed to load audio:', audioUrl, error)
      return null
    }
  }

  /**
   * Get audio buffer by URL from cache
   * @param audioUrl - The URL of the audio file
   * @returns AudioBuffer if cached, null otherwise
   */
  getAudio(audioUrl: string): AudioBuffer | null {
    if (this.audioCache.has(audioUrl)) {
      // console.log('🔊 Found audio in cache:', audioUrl)
      return this.audioCache.get(audioUrl)!
    } else {
      console.warn('Audio not found in cache:', audioUrl)
      return null
    }
  }

  /**
   * Create a PositionalAudio object from an audio URL
   * @param audioUrl - The URL of the audio file
   * @param listener - The AudioListener to attach to
   * @returns Promise that resolves to PositionalAudio
   */
  async createPositionalAudio(audioUrl: string, listener: THREE.AudioListener): Promise<THREE.PositionalAudio | null> {
    try {
      const audioBuffer = await this.loadAudio(audioUrl)
      if (!audioBuffer) {
        return null
      }

      const positionalAudio = new THREE.PositionalAudio(listener)
      positionalAudio.setBuffer(audioBuffer)
      
      // console.log('✅ PositionalAudio created successfully:', audioUrl)
      return positionalAudio
      
    } catch (error) {
      console.error('Failed to create PositionalAudio:', audioUrl, error)
      return null
    }
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    // Clean up DRACO loader
    if (this.dracoLoader) {
      this.dracoLoader.dispose()
    }
    
    // Clear caches
    this.materialCache.clear()
    this.textureCache.clear()
    this.audioCache.clear()
    
    // console.log('✅ AssetManager disposed')
  }

  /**
   * Get material database
   */
  getMaterialDatabase(): MaterialDatabase | null {
    return this.materialDatabase
  }

  /**
   * Get material cache
   */
  getMaterialCache(): Map<string, THREE.Material> {
    return this.materialCache
  }

  /**
   * Get texture by name from cache or create new instance from database
   * @param textureName - The name/token of the texture to fetch
   * @returns The texture instance or null if not found
   */
  getTexture(textureName: string): THREE.Texture | null {
    if (!this.materialDatabase?.global?.textures) {
      console.warn('No material database available, cannot fetch texture:', textureName)
      return null
    }

    // Look for texture definition in database
    const textureDef = this.materialDatabase.global.textures[textureName]
    
    if (textureDef) {
      // console.log(`🖼️ Fetching texture from database: ${textureName}`)
      
      // Use the existing loadTexture method which handles caching
      return this.loadTexture(textureDef)
    } else {
      console.warn(`No texture definition found for: ${textureName}`)
      return null
    }
  }

  /**
   * Get texture cache
   */
  getTextureCache(): Map<string, THREE.Texture> {
    return this.textureCache
  }

  /**
   * Get audio cache
   */
  getAudioCache(): Map<string, AudioBuffer> {
    return this.audioCache
  }

  async loadAudioAssets(): Promise<void> {
    // console.log('🔊 Starting to load audio assets...')
    
    // Loop through all AudioAssetType entries
    for (const [key, filename] of Object.entries(AudioAssetMap)) {
      const audioUrl = `audio/${filename}`
      // console.log(`🔊 Loading audio asset: ${key} -> ${audioUrl}`)
      
      try {
        const audioBuffer = await this.loadAudio(audioUrl)
        if (audioBuffer) {
          // console.log(`✅ Successfully loaded audio asset: ${key}`)
        } else {
          console.warn(`Failed to load audio asset: ${key}`)
        }
      } catch (error) {
        console.error(`Error loading audio asset ${key}:`, error)
      }
    }
    
    // console.log(`🔊 Audio assets loading complete. Cache size: ${this.audioCache.size}`)
  }

  /**
   * Get audio buffer by AudioAssetType key
   * @param audioKey - The AudioAssetType key
   * @returns AudioBuffer if found, null otherwise
   */
  getAudioAsset(audioKey: keyof typeof AudioAssetMap): AudioBuffer | null {
    const filename = AudioAssetMap[audioKey as keyof typeof AudioAssetMap]
    const audioUrl = `audio/${filename}`
    return this.getAudio(audioUrl)
  }

  /**
   * Get the loaded environment texture
   * @returns The HDR environment texture or null if not loaded
   */
  getEnvironmentTexture(): THREE.Texture | null {
    return this.environmentTexture
  }

}
